import type { Client, Exchange, Operation } from '@urql/core';
import { createClient, fetchExchange, makeOperation } from '@urql/core';
import { devtoolsExchange } from '@urql/devtools';
import { authExchange } from '@urql/exchange-auth';
import { cacheExchange } from '@urql/exchange-graphcache';
import { pipe, map } from 'wonka';
import { isRef, ref, unref } from 'vue';
import merge from 'lodash/merge';
import { safeLocalStorage } from '@teachino/shared-util';
import type { AuthResult, GraphCacheConfig } from '~/graphql';
import { RefreshTokenDocument } from '~/graphql';
import { emailNotificationSubscriptionCacheExchange } from '~/stores/emailNotificationSubscriptionStore';
import { itemCacheExchange } from '~/stores/itemManagement';
import { useSession } from '~/stores/session';
import { topicInstancesCacheExchange } from '~/stores/topicInstanceManagement';
import { topicsCacheExchange } from '~/stores/topicManagement';
import { weeklyPlanningManagementCacheExchange } from '~/stores/weeklyPlanningManagement';

import introspection from '~/graphql/__generated__/introspection';
import { courseAiSettingCacheExchange } from '~/stores/courseAiSettingManagement';
import { courseCacheExchange } from '~/stores/courseManagement';
import { courseMaterialFoldersCacheExchange } from '~/stores/courseMaterialFoldersCacheExchange';
import { integrationCacheExchange } from '~/stores/integrationStore';
import { apiUrl } from '~/utils/constants';

const graphqlEndpoint = `${apiUrl}/graphql`;

function sanitizeReactiveValue(value: unknown, seen = new WeakMap()): unknown {
  const plainValue = isRef(value) ? unref(value) : value;

  if (
    plainValue === null ||
    plainValue === undefined ||
    typeof plainValue !== 'object'
  ) {
    return plainValue;
  }

  if (plainValue instanceof Date) {
    return plainValue;
  }

  if (seen.has(plainValue)) {
    return seen.get(plainValue);
  }

  if (Array.isArray(plainValue)) {
    const sanitizedArray: unknown[] = [];
    seen.set(plainValue, sanitizedArray);
    for (const entry of plainValue) {
      sanitizedArray.push(sanitizeReactiveValue(entry, seen));
    }
    return sanitizedArray;
  }

  const sanitizedObject: Record<string, unknown> = {};
  seen.set(plainValue, sanitizedObject);

  for (const [key, entry] of Object.entries(plainValue)) {
    sanitizedObject[key] = sanitizeReactiveValue(entry, seen);
  }

  return sanitizedObject;
}

const sanitizeOperationForDevtoolsExchange: Exchange =
  ({ forward }) =>
  (ops$) =>
    pipe(
      ops$,
      map((operation: Operation) =>
        makeOperation(
          operation.kind,
          {
            key: operation.key,
            query: operation.query,
            variables: sanitizeReactiveValue(operation.variables) as Operation['variables'],
          },
          {
            ...operation.context,
            meta: sanitizeReactiveValue(operation.context.meta),
          },
        ),
      ),
      forward,
    );

export function formatBearerToken(token: string) {
  return `Bearer ${token}`;
}

export function deleteTokens() {
  safeLocalStorage.removeItem('token');
  safeLocalStorage.removeItem('refreshToken');
}

function getTokens() {
  const token = safeLocalStorage.getItem('token');
  const refreshToken = safeLocalStorage.getItem('refreshToken');
  return { token, refreshToken };
}

export function storeTokens({ access_token, refresh_token }: AuthResult) {
  safeLocalStorage.setItem('token', formatBearerToken(access_token));
  safeLocalStorage.setItem('refreshToken', refresh_token);
  return {
    token: formatBearerToken(access_token),
    refresh_token,
  };
}

export default defineNuxtPlugin((nuxtApp) => {
  const authFreeEndpoints = [
    'login',
    'completeTwoFactorChallenge',
    'signup',
    'requestSignupV2Code',
    'requestLoginCode',
    'checkAccountEmail',
    'verifySignupV2Code',
    'publicCourse',
    'publicCourseCalendarDays',
    'publicMaterial',
    'publicItemInstance',
    'itemForItemInstance',
    'courseInvitePreview',
    'integrationCheck',
    'connectExternalIntegration',
    'requestUserPasswordReset',
    'verifyUserPasswordReset',
    'tenantInvitePreview',
    'aiToolTextToSpeech',
    'ssoSignup',
    'studentAssignmentOpenQuestionHistory',
    'studentMaterialEvaluationTeacherFeedback',
    'publicCourseFolders',
    'publicCourseFeatures',
    'publicMaterialAnnotationToolAccess',
    'publicSharedMaterial',
    'currentDpaDocument',
    'publicDpaPreview',
    'requestPublicDpaCode',
    'confirmPublicDpaSigning',
    'courseStudentSelfRegistrationStatus',
    'selfRegisterCourseStudent',
  ];
  // use urql graphcache
  const cacheConfig: GraphCacheConfig = {
    schema: introspection,
    keys: {
      UserResult: () => null,
      TopicInstanceResult: () => null,
      UserCoursePermission: () => null,
      UserTenantPermission: () => null,
      UserTopicPermission: () => null,
      UserItemPermission: () => null,
      TopicInstanceStatistics: () => null,
      CourseListStatistics: () => null,
      CourseContentVisibility: () => null,
      GlobalConfiguration: () => null,
      TopicStatistics: () => null,
      Configuration: () => null,
      Feature: () => null,
      UserEmailNotificationSubscription: () => null,
      ExternalCoursesResult: () => null,
      UnmappedCourse: () => null,
      ConnectedCourse: () => null,
      ExternalCourseStudentImportSource: () => null,
      ExternalCourseStudentImportSourcesResult: () => null,
      ExternalCourseStudentImportAvailabilityResult: () => null,
      TimetablePeriod: () => null,
      TimetableGridEntry: () => null,
      Timetable: () => null,
      FeatureFlag: () => null,
      TimetableDayResult: () => null,
      TimetableDayError: () => null,
      SchoolYearTimetableResult: (data) => data.schoolYearId,
      SchoolYearTimetableWeekAssignment: () => null,
      LibraryMetaResult: () => null,
      SupportTokenResult: () => null,
      TopicContent: () => null,
      ItemContent: () => null,
      MaterialContentHtml: () => null,
      MaterialContentStatistics: () => null,
      // Keep materials embedded in course folder lists so updateMaterial or a
      // refetch cannot overwrite another card's updatedAt/contentStatistics via
      // normalized cache entity sharing.
      Material: () => null,
      MaterialCourse: () => null,
      MaterialCoursesForMaterial: () => null,
      // Keep course folder rows embedded so list refetches do not merge shared
      // material snapshots across cards.
      CourseMaterial: () => null,
      AiMaterialApproval: () => null,
      OriginDetails: () => null,
      LibraryNewestContentResult: () => null,
      AssignmentStatus: () => null,
      MaterialInteractiveAssignmentsStudentOverview: () => null,
      AssignmentOpenQuestionHistory: () => null,
      MaterialInteractiveAssignmentStatus: () => null,
      MaterialEvaluationDashboard: () => null,
      MaterialEvaluationDashboardCourse: (data) => data.id ?? null,
      EvaluationScoredTaskDefinition: (data) => data.id ?? null,
      EvaluationProgressTaskDefinition: (data) => data.id ?? null,
      EvaluationScoredTaskStatus: () => null,
      EvaluationDefaultScoredTaskStatus: () => null,
      EvaluationOpenQuestionScoredTaskStatus: () => null,
      EvaluationProgressTaskStatus: () => null,
      EvaluationDefaultProgressTaskStatus: () => null,
      EvaluationChatbotProgressTaskStatus: () => null,
      EvaluationFileUploadProgressTaskStatus: () => null,
      EvaluationDashboardTaskColumn: () => null,
      // A session belongs to a student and is shared across materials. Keep
      // dashboard students embedded so the status from one material cannot
      // overwrite the same student's status from another material.
      EvaluationDashboardStudent: () => null,
      EvaluationStudentSummary: () => null,
      // Not keyed by sessionId: a session is shared by the same student
      // across all materials (sessions are per-student, not per-material),
      // so normalizing by sessionId let one material's cached detail
      // overwrite another's, causing the dialog to flash stale content
      // from a different material before cache-and-network refetches.
      MaterialEvaluationStudentDetail: () => null,
      EvaluationTaskSection: () => null,
      EvaluationOpenQuestionTaskDetail: () => null,
      EvaluationOpenQuestionAttempt: () => null,
      EvaluationMultipleChoiceTaskDetail: () => null,
      EvaluationMultipleChoiceAttempt: () => null,
      EvaluationGapTextTaskDetail: () => null,
      EvaluationGapTextAttempt: () => null,
      EvaluationMatchingTaskDetail: () => null,
      EvaluationMatchingAttempt: () => null,
      EvaluationChatbotTaskDetail: () => null,
      EvaluationChatbotInstance: (data) => data.instanceId ?? null,
      EvaluationFileUploadTaskDetail: () => null,
      AssignmentFileUploadAttempt: () => null,
      AssignmentFileUploadFile: (data) => data.key ?? null,
      StudentAssignmentOpenQuestionHistory: () => null,
      AssignmentOpenQuestionAttempt: () => null,
      AssignmentMatchingHistory: () => null,
      AssignmentMatchingAttempt: () => null,
      AssignmentMatchingConnectionResult: () => null,
      AssignmentMatchingMissingConnection: () => null,
      MaterialAnnotationToolAccess: () => null,
      CourseMaterialVisibilityDetailsResult: () => null,
      CourseMaterialVisibilityDetailsBatchResult: () => null,
      CourseMaterialVisibilityDetails: (data) => data.id ?? null,
      MaterialVisibilitySchedule: () => null,
      StudentVisibilitySchedule: () => null,
      ShiftCoursePlanningResult: () => null,
      CourseStudentsResult: (data) => data.courseId ?? null,
      CourseStudentGroupsResult: (data) => data.courseId ?? null,
      CourseStudentShareDetails: () => null,
      CourseStudentSelfRegistrationStatus: () => null,
      CourseStudentSelfRegistrationResult: () => null,
      CourseGradingOverviewOutput: () => null,
      CourseGradingSettingsOutput: () => null,
      StudentDocumentationDayCountsOutput: () => null,
      CourseGradingOverviewRowOutput: (data) => data.courseStudentId ?? null,
      CourseMaterialGradeOutput: (data) => data.id ?? null,
      StudentNoteTagCountOutput: () => null,
      GradingSchemeOutput: (data) => data.schemeKey ?? null,
      GradingSchemeValueOutput: () => null,
      DpaDocumentOutput: () => null,
      DpaFlowOutput: () => null,
      DpaPublicPreviewOutput: () => null,
    },
    ...merge(
      topicInstancesCacheExchange,
      itemCacheExchange,
      weeklyPlanningManagementCacheExchange,
      emailNotificationSubscriptionCacheExchange,
      courseAiSettingCacheExchange,
      topicsCacheExchange,
      courseCacheExchange,
      courseMaterialFoldersCacheExchange,
      integrationCacheExchange,
    ),
  };

  const urqlClient: Client = createClient({
    url: graphqlEndpoint,
    // set default request policy
    requestPolicy: 'cache-and-network',
    // Force all requests to use POST method
    preferGetMethod: false,
    fetchOptions: {
      method: 'POST',
    },
    exchanges: [
      sanitizeOperationForDevtoolsExchange,
      devtoolsExchange,
      /* debugExchange,*/
      cacheExchange(cacheConfig),
      authExchange(async (utils) => {
        let { token, refreshToken } = getTokens();
        let refreshCallCount = 0;
        const session = useSession();
        return {
          didAuthError(error, operation) {
            return error.graphQLErrors.some(
              (e) => e.extensions.code === 'UNAUTHENTICATED',
            );
          },
          willAuthError(operation) {
            // https://formidable.com/open-source/urql/docs/advanced/authentication/#configuring-willautherror
            token = safeLocalStorage.getItem('token');
            if (!token) {
              // this will only run if the token isn't true
              const innerVals = [];
              const val = !operation.query.definitions.some(
                function (definition) {
                  return (
                    definition.kind === 'OperationDefinition' &&
                    definition.selectionSet.selections.some((node) => {
                      innerVals.push(node.name.val);
                      return (
                        node.kind === 'Field' &&
                        authFreeEndpoints.includes(node.name.value)
                      );
                    })
                  );
                },
              );
              return val;
              // if the operation is not one of the auth free endpoints it will result in an error
            }
            //TODO EVALUATE EXPIRATION DIRECTLY (Return true if either of the tokens have expired)
            return false;
          },
          addAuthToOperation(operation) {
            if (!token) {
              return operation;
            }

            const tokenString = token.includes('Bearer')
              ? token
              : formatBearerToken(token);

            const fetchOptions =
              typeof operation.context.fetchOptions === 'function'
                ? operation.context.fetchOptions()
                : operation.context.fetchOptions || {};

            return makeOperation(operation.kind, operation, {
              ...operation.context,
              fetchOptions: {
                ...fetchOptions,
                headers: {
                  ...fetchOptions.headers,
                  Authorization: tokenString,
                },
              },
            });
          },
          async refreshAuth() {
            // called when auth error has occurred
            // we update refresh the token with a GraphQL mutation
            if (refreshCallCount > 3) {
              session.logOutAndRedirect();
              return;
            }

            refreshCallCount++;

            try {
              refreshToken = safeLocalStorage.getItem('refreshToken');

              const result = await utils.mutate(RefreshTokenDocument, {
                refreshTokenInput: {
                  refreshToken,
                },
              });

              if (result.data?.refreshToken) {
                token = result.data.refreshToken.access_token;
                refreshToken = result.data.refreshToken.refresh_token;
                storeTokens(result.data.refreshToken);
                refreshCallCount = 0;
              } else {
                session.logOutAndRedirect();
                return;
              }
            } catch (e) {
              session.logOutAndRedirect();
              console.log('Error refreshing token');
              console.log(e);
            }
          },
        };
      }),
      fetchExchange,
    ],
  });

  nuxtApp.provide('urql', urqlClient);
  const { vueApp } = nuxtApp;
  vueApp.provide('$urql', ref(urqlClient));
});
